import { Rect } from "@/lib/ai/detection/face-detection"
import {
	getAllFaceBounds,
	getFaceArea,
	getRectCenter,
	getMatchingFaceIndex,
	radialMaskToRect,
	percentDifference,
	meanAbsoluteDifference,
	getShrinkedFaces,
} from "./ai-calibration-utils"
import { Adjustments, MaskGroup, Mask, RadialMask, MaskAdjustmentKey } from "@/core/types"
import { getWeightedSum, ImageFeature } from "./ai-calibration-feature"
import equal from "fast-deep-equal"
import { clamp, transformLocalAdjustment, transformMaskForCopyPaste } from "@/utils/math"
import { getDefaultAdjustments, getLocalAdjustmentRange } from "@/core/adjustments"
import { vec2 } from "gl-matrix"
import { TransferInfo } from "./ai-calibration-keyadj"
import { logAiInfo } from "@/lib/ai/utils"
import {
	AlignInfo,
	transformInUncroppedSpace,
	transformMasks,
} from "@/lib/ai/alignment/ai-image-align"
import { ImageInfoRecord } from "@/lib/catalog"
import { FlowResult } from "@/lib/ai/detection/optical-flow"

const linearMaskThresh = 0.14
const aiMaskThresh = 0.2

export interface ClassifiedLocalAdjustments {
	preset: MaskGroup[]
	modifiedPreset: MaskGroup[]
	manual: MaskGroup[]
}

export const localParamsKeys = [
	"temperature",
	"tint",
	"saturation",
	"toningHue",
	"toningSaturation",
	"exposure",
	"contrast",
	"highlights",
	"shadows",
	"whites",
	"blacks",
	"clarity",
	"texture",
	"dehaze",
] as const satisfies readonly MaskAdjustmentKey[]

export function isPresetMaskUpdated(
	userLocalAdjustments: MaskGroup[] | undefined,
	presetLocalAdjustments: MaskGroup[] | undefined,
) {
	let updated = false
	const keptLocalAdjmts: MaskGroup[] = []

	if (!presetLocalAdjustments) return { updated, keptLocalAdjmts }
	else if (!userLocalAdjustments) return { updated: true, keptLocalAdjmts }

	const userLocalAdjMap = userLocalAdjustments.reduce((map, adjustment) => {
		map[adjustment.id] = adjustment
		return map
	}, {})

	for (const presetLocal of presetLocalAdjustments) {
		const userLocal = userLocalAdjMap[presetLocal.id]
		if (!userLocal) {
			// deleted
			updated = true
		} else {
			keptLocalAdjmts.push(userLocal) // kept
			if (!isLocalAdjustmentEqual(userLocal, presetLocal)) {
				// modified
				updated = true
			}
		}
	}

	return { updated, keptLocalAdjmts }
}

export function getClassifiedLocalAdjustments(
	userLocalAdjustments: MaskGroup[] | undefined,
	presetLocalAdjustments: MaskGroup[] | undefined,
): ClassifiedLocalAdjustments | undefined {
	if (!userLocalAdjustments) return undefined

	const presetLocalAdjMap = presetLocalAdjustments
		? presetLocalAdjustments.reduce((map, adjustment) => {
				map[adjustment.id] = adjustment
				return map
			}, {})
		: {}

	const manualLocalAdjustments: MaskGroup[] = []
	const modifiedPresetLocalAdjustments: MaskGroup[] = []
	const unmodifiedPresetLocalAdjustments: MaskGroup[] = []

	for (const userLocal of userLocalAdjustments) {
		const presetLocal = presetLocalAdjMap[userLocal.id]
		if (presetLocal) {
			if (isLocalAdjustmentEqual(userLocal, presetLocal)) {
				unmodifiedPresetLocalAdjustments.push(userLocal)
			} else {
				modifiedPresetLocalAdjustments.push(userLocal)
			}
		} else {
			manualLocalAdjustments.push(userLocal)
		}
	}

	return {
		preset: unmodifiedPresetLocalAdjustments,
		modifiedPreset: modifiedPresetLocalAdjustments,
		manual: manualLocalAdjustments,
	}
}

function isLocalAdjustmentEqual(local1: MaskGroup, local2: MaskGroup) {
	const local1Copy = { ...local1, masks: [] }
	const local2Copy = { ...local2, masks: [] }
	if (!equal(local1Copy, local2Copy)) {
		return false
	} else {
		return isMasksEqual(local1.masks, local2.masks)
	}
}

// Allow minor numerical deviation for radial/gradient mask due to transform math numerical error
function isMasksEqual(masks1: Mask[], masks2: Mask[], eps: number = 0.001) {
	if (masks1.length !== masks2.length) {
		return false
	}

	for (let i = 0; i < masks1.length; i++) {
		const mask1 = masks1[i] as Mask
		const mask2 = masks2[i] as Mask
		if (mask1.type === "radial" || mask1.type === "gradient") {
			for (const key of Object.keys(mask1)) {
				if (typeof mask1[key] === "number" && typeof mask2[key] === "number") {
					if (percentDifference(mask1[key], mask2[key]) > eps) {
						return false
					}
				} else if (mask1[key] !== mask2[key]) {
					return false
				}
			}
		} else if (!equal(mask1, mask2)) {
			return false
		}
	}

	return true
}

export function getLocalAdjustmentsBiases(
	classifiedLocalAdjustments: ClassifiedLocalAdjustments,
	presetLocalAdjustments: MaskGroup[] | undefined,
): MaskGroup[] {
	if (!presetLocalAdjustments) return []

	const presetLocalAdjMap = presetLocalAdjustments
		? presetLocalAdjustments.reduce((map, adjustment) => {
				map[adjustment.id] = adjustment
				return map
			}, {})
		: {}

	const biases: MaskGroup[] = []

	for (const userLocal of classifiedLocalAdjustments.modifiedPreset) {
		const presetLocal = presetLocalAdjMap[userLocal.id]
		if (presetLocal) {
			const bias: MaskGroup = { ...userLocal }
			for (const key of localParamsKeys) {
				const userLocalValue = userLocal[key]
				const presetLocalValue = presetLocal[key]
				if (
					typeof bias[key] === "number" &&
					typeof userLocalValue === "number" &&
					typeof presetLocalValue === "number"
				)
					bias[key] = userLocalValue - presetLocalValue
			}
			biases.push(bias)
		}
	}

	for (const userLocal of classifiedLocalAdjustments.preset) {
		const bias: MaskGroup = { ...userLocal }
		for (const key of localParamsKeys) {
			if (typeof bias[key] === "number") bias[key] = 0
		}
		biases.push(bias)
	}

	return biases
}

export function computeWeightedLocalBiases(
	presetLocalBiases: MaskGroup[],
	allRefsLocalBiases: MaskGroup[][],
	weights: number[],
): MaskGroup[] {
	const n = Math.min(allRefsLocalBiases.length, weights.length)

	const biases: MaskGroup[] = []
	for (const singlePresetLocal of presetLocalBiases) {
		const presetLocalId = singlePresetLocal.id
		const allRefsBiasForPreset: MaskGroup[] = []
		for (let i = 0; i < n; i++) {
			const refLocalBiases = allRefsLocalBiases[i] as MaskGroup[]
			let refBiasForPreset = refLocalBiases.find((val) => val.id === presetLocalId)
			if (!refBiasForPreset) {
				const zeroLocalBias: MaskGroup = { ...singlePresetLocal }
				for (const key of localParamsKeys) {
					if (typeof zeroLocalBias[key] === "number") zeroLocalBias[key] = 0
				}
				refBiasForPreset = zeroLocalBias
			}
			allRefsBiasForPreset.push(refBiasForPreset)
		}
		const bias = { ...singlePresetLocal }
		for (const key of localParamsKeys) {
			if (typeof bias[key] === "number") {
				const allRefsBias = allRefsBiasForPreset.map((val) => val[key])
				bias[key] = getWeightedSum(allRefsBias, weights)
			}
		}
		biases.push(bias)
	}

	return biases
}

export function applyLocalAdjustmentsBias(adjustmentValue: MaskGroup[], biasValue: MaskGroup[]) {
	for (const presetLocalAdj of adjustmentValue) {
		const bias = biasValue.find((val) => val.id === presetLocalAdj.id)
		if (bias) {
			for (const key of localParamsKeys) {
				const presetVal = presetLocalAdj[key]
				const biasVal = bias[key]
				if (typeof presetVal === "number" && typeof biasVal === "number") {
					presetLocalAdj[key] = clamp(
						presetVal + biasVal,
						...getLocalAdjustmentRange(key),
					)
				}
			}
		}
	}

	return adjustmentValue
}

export function transferLocalAdjustments(
	info: TransferInfo,
	keyAdjustments: Partial<Adjustments>,
	flowResult: FlowResult | undefined | null,
): MaskGroup[] | null {
	const { imageInfo, keyImageInfo, keyImageCurrentAdjustments, dScene } = info

	// use localAdjustmentsManual in biases to distinguish it from preset-local-adjustments
	const localAdjustments = keyImageInfo.biases ? keyImageInfo.biases.localAdjustmentsManual : null

	// no manual local adjustment cases:
	if (!localAdjustments || localAdjustments.length === 0) return null

	if (dScene > aiMaskThresh) return null

	const feature = imageInfo.features
	const keyFeature = keyImageInfo.features
	if (!feature || !keyFeature) {
		return null
	}
	const oriInfo = getImageOrientationInfo(feature)
	const defaultAdjustments = getDefaultAdjustments()
	const cropAdjustments = { ...defaultAdjustments, ...keyAdjustments } // adapted crop/straighten

	const result: MaskGroup[] = []

	for (const localAdjustmentOnScreen of localAdjustments) {
		// find original localAdjustment where the coords are in uncropped space
		const localAdjustment = keyImageCurrentAdjustments.localAdjustments?.find(
			(val) => val.id === localAdjustmentOnScreen.id,
		)
		if (!localAdjustment) continue

		logAiInfo("transferLocalAdjustment", {
			id: imageInfo.imageId,
			keyID: keyImageInfo.imageId,
			dScene,
		})

		/** First transform masks based on global or local alignment */

		const alignedLocalAdjustment = alignLocalAdjustment(
			localAdjustment,
			imageInfo,
			flowResult,
			dScene,
		)

		// if local mask is alignable, directly return it
		if (alignedLocalAdjustment) {
			result.push(alignedLocalAdjustment)
			continue
		}

		/** Not alignable cases below */

		// transform mask for paste: localAdjustmentOnScreen already transformed for copy in approve()
		const localAdjustmentToPaste = transformLocalAdjustment(
			localAdjustmentOnScreen,
			cropAdjustments,
			oriInfo.originalImageSize,
			oriInfo.initialOrientation,
			false,
		)

		// For more than 1 masks: only transfer if all are ai masks or ai+linear masks.
		if (localAdjustmentOnScreen["masks"].length > 1) {
			const maskTypeCount: Record<string, number> = {}
			for (const maskItem of localAdjustmentOnScreen["masks"]) {
				const mtype = generalMaskType(maskItem)
				maskTypeCount[mtype] = (maskTypeCount[mtype] ?? 0) + 1
			}

			if (maskTypeCount["ai"] === localAdjustmentOnScreen["masks"].length) {
				result.push(localAdjustmentToPaste)
			} else if (
				!maskTypeCount["radial"] &&
				!maskTypeCount["brush"] &&
				dScene < linearMaskThresh
			) {
				result.push(localAdjustmentToPaste)
			}
			continue
		}

		if (localAdjustment["masks"].length === 0) continue

		// Single mask case:
		const mask = localAdjustment["masks"][0] as Mask

		switch (mask.type) {
			case "background":
			case "people":
			case "sky":
			case "subject":
				result.push(localAdjustmentToPaste)
				break
			case "gradient": {
				if (dScene < linearMaskThresh) {
					result.push(localAdjustmentToPaste)
				}
				break
			}
			case "radial": {
				// radial mask (try to match face):
				const matchedLocalAdjustment = transferRadialMaskByFace({
					originalMask: mask,
					localAdjustment,
					feature,
					keyFeature,
					flowResult,
				})
				if (matchedLocalAdjustment) {
					result.push(matchedLocalAdjustment)
				}
				break
			}
			case "brush":
				break
		}
	}

	return result
}

export function alignLocalAdjustment(
	localAdjustment: MaskGroup,
	imageInfo: ImageInfoRecord,
	flowResult: FlowResult | null | undefined,
	dScene: number,
) {
	if (flowResult === null) return null

	// for getKeyAdjustmentsFast
	if (flowResult === undefined) return { ...localAdjustment }

	const result = { ...localAdjustment }
	const tranformOptions = {
		flowResult,
		orientedSize: imageInfo.features.imageSize,
		initialOrientation: imageInfo.features.initialOrientation ?? 0,
		dScene,
	}

	const newMasks: Mask[] = transformMasks(result.masks, tranformOptions)
	if (newMasks.length === 0) return null

	result.masks = newMasks

	logAiInfo("alignLocalAdjustments", {
		localAdjustment,
		alignedLocalAdjustment: result,
	})

	return result
}

function generalMaskType(mask: Mask): string {
	if (
		mask.type === "background" ||
		mask.type === "people" ||
		mask.type === "sky" ||
		mask.type === "subject"
	) {
		return "ai"
	} else {
		return mask.type
	}
}

export function getImageOrientationInfo(feature: ImageFeature) {
	const initialOrientation = feature.initialOrientation ?? 0
	const [w, h] = feature.imageSize ?? [400, 300] // oriented
	const originalImageSize: vec2 = Math.abs(initialOrientation) % 2 ? [h, w] : [w, h]

	return { originalImageSize, initialOrientation }
}

function transferRadialMaskByFace(options: {
	originalMask: RadialMask // in uncropped/no-orientation space
	localAdjustment: MaskGroup // in uncropped/no-orientation space
	feature: ImageFeature
	keyFeature: ImageFeature
	flowResult: FlowResult | undefined | null
}): MaskGroup | null {
	// Do face matching in initial-oriented space (face coords are already in this space)

	const { originalMask, localAdjustment, feature, keyFeature, flowResult } = options
	const keyOriInfo = getImageOrientationInfo(keyFeature)
	const oriInfo = getImageOrientationInfo(feature)

	// transform mask to initial-oriented space
	const orientedMask = transformMaskForCopyPaste(
		originalMask,
		keyOriInfo.originalImageSize,
		getDefaultAdjustments(),
		keyOriInfo.initialOrientation,
		true,
	) as RadialMask
	const maskRect = radialMaskToRect(orientedMask)

	const faceCount = feature.faceBoxCoords.length
	const keyFaceCount = keyFeature.faceBoxCoords.length

	// First shrink face box width to better fit with actual faces
	const keyFaceCoords = getShrinkedFaces(keyFeature.faceBoxCoords)
	logAiInfo("oriented mask", {
		keyFaceCoords,
		originalMask,
		orientedMask,
	})

	const keyFaceIndex = getMatchingFaceIndex({
		targetRect: maskRect,
		faceBoxCoords: keyFaceCoords,
	})
	logAiInfo("matched key face", { keyFaceIndex, maskRect, maskDesc: originalMask })

	// if both have face and mask mapped to a face or all faces, try to find the same face(s)
	// and adjust the mask coord according to face coords
	if (keyFaceIndex !== -1 && faceCount > 0 && keyFaceCount > 0) {
		const faceLightDiff = meanAbsoluteDifference(
			feature.light.facePercentiles,
			keyFeature.light.facePercentiles,
		)

		const faceCoords = getShrinkedFaces(feature.faceBoxCoords)

		// translate key faces coords according to alignment
		let alignedKeyFaceCoords = keyFaceCoords
		if (flowResult) {
			alignedKeyFaceCoords = alignFaceCoords(keyFaceCoords, {
				orientedSize: keyFeature.imageSize,
				initialOrientation: 0,
				flowResult,
			})
		}

		const adjustedMask = getAdjustedFaceMask({
			faceCoords,
			keyFaceCoords,
			alignedKeyFaceCoords,
			keyFaceIndex,
			orientedMask,
			faceLightDiff,
		})

		if (adjustedMask) {
			logAiInfo("matched face(s) - new radial mask oriented", { adjustedMask })
			// transform for paste (to no-orientation space)
			const result = structuredClone(localAdjustment)
			result["masks"][0] = transformMaskForCopyPaste(
				adjustedMask,
				oriInfo.originalImageSize,
				getDefaultAdjustments(),
				oriInfo.initialOrientation,
				false,
			)

			return result
		}
	}

	return null
}

function getAdjustedFaceMask(options: {
	faceCoords: Rect[]
	keyFaceCoords: Rect[]
	alignedKeyFaceCoords: Rect[]
	keyFaceIndex: number
	orientedMask: RadialMask
	faceLightDiff: number
}) {
	const {
		faceCoords,
		keyFaceCoords,
		alignedKeyFaceCoords,
		keyFaceIndex,
		orientedMask,
		faceLightDiff,
	} = options

	const faceCount = faceCoords.length
	const keyFaceCount = keyFaceCoords.length

	const matchingOptions = {
		dThresh: 0.15, // dx, dy thresh for matching face
		widthThresh: 0.3, // face width percent-diff thresh for matching face
		widthThreshAllFaces: 0.3,
		checkY: true,
		includeAllFaceRect: false,
	}

	// mask matched to all-faces rect in keyPhoto
	if (keyFaceIndex >= keyFaceCount) {
		if (faceCount === keyFaceCount && faceCount <= 3) {
			// try to adjust the coord of mask
			const allFaceBounds = getAllFaceBounds(faceCoords)
			const allFaceArea = getFaceArea(allFaceBounds)

			const keyAllFaceBounds = getAllFaceBounds(alignedKeyFaceCoords)
			const keyAllFaceArea = getFaceArea(keyAllFaceBounds)

			const faceAreaDiff = percentDifference(allFaceArea, keyAllFaceArea)

			const matchingIndex = getMatchingFaceIndex({
				...matchingOptions,
				targetRect: keyAllFaceBounds,
				faceBoxCoords: [allFaceBounds],
			})

			// assuming same faces found
			if (matchingIndex !== -1 && faceAreaDiff < 0.4) {
				logAiInfo("found matching all face", {
					matchingIndex,
					allFaceBounds,
					keyAllFaceBounds,
				})

				const maskNew = adjustMaskCoordinate(
					orientedMask,
					allFaceBounds,
					getAllFaceBounds(keyFaceCoords),
				)
				return maskNew
			}
		}
	} else {
		// mask mapped to an individual face in keyPhoto, must find a matching face to transfer mask.
		const matchedKeyFaceAligned = alignedKeyFaceCoords[keyFaceIndex] as Rect
		let matchingIndex = getMatchingFaceIndex({
			...matchingOptions,
			targetRect: matchedKeyFaceAligned,
			faceBoxCoords: faceCoords,
		})

		// if not matched but has a single face, transfer mask if light is similar and area not too different
		if (matchingIndex === -1 && faceCoords.length === 1 && keyFaceCoords.length === 1) {
			const area1 = getFaceArea(matchedKeyFaceAligned)
			const area2 = getFaceArea(faceCoords[0] as Rect)
			const areaDiff = percentDifference(area1, area2)
			logAiInfo("1 face not matched:", { areaDiff, faceLightDiff })
			if (
				(faceLightDiff < 0.08 && areaDiff < 0.5) ||
				(faceLightDiff < 0.12 && areaDiff < 0.2)
			) {
				matchingIndex = 0
			}
		}

		// if matched
		if (matchingIndex !== -1) {
			const matchingFace = faceCoords[matchingIndex] as Rect
			const matchedKeyFace = keyFaceCoords[keyFaceIndex] as Rect
			const maskNew = adjustMaskCoordinate(orientedMask, matchingFace, matchedKeyFace)
			logAiInfo("found matching 1 face", { matchingIndex, matchingFace, matchedKeyFace })

			return maskNew
		}
	}

	return null
}

function adjustMaskCoordinate(maskDesc: RadialMask, face: Rect, keyFace: Rect) {
	const faceCenter = getRectCenter(face)
	const keyFaceCenter = getRectCenter(keyFace)

	const maskOrientation = Math.round(Math.abs(maskDesc.angle) / 90)

	// scale the radial mask
	const scaleX = face.width / keyFace.width
	const scaleY = face.height / keyFace.height

	// move center to align mask with face on X-axis; not align face with keyFace due to side-face coord inaccuracy
	const dx = faceCenter[0] - maskDesc.centerX

	const actualRadiusY = maskOrientation % 2 ? maskDesc.radiusX : maskDesc.radiusY
	const yOffsetRatio = (maskDesc.centerY - keyFaceCenter[1]) / actualRadiusY
	const newY = actualRadiusY * scaleY * yOffsetRatio + faceCenter[1]

	const resultMaskDesc = structuredClone(maskDesc)
	resultMaskDesc.centerX += dx
	resultMaskDesc.centerY = newY
	resultMaskDesc.radiusX *= maskOrientation % 2 ? scaleY : scaleX
	resultMaskDesc.radiusY *= maskOrientation % 2 ? scaleX : scaleY

	return resultMaskDesc
}

function alignFaceCoords(faces: Rect[], tranformOptions: AlignInfo) {
	// align by translating center
	const alignedFaces: Rect[] = []
	for (const face of faces) {
		const faceCenter: vec2 = [face.x + face.width / 2, face.y + face.height / 2]
		const newFaceCenter = transformInUncroppedSpace(faceCenter, tranformOptions)
		const newFace = {
			...face,
			x: newFaceCenter[0] - face.width / 2,
			y: newFaceCenter[1] - face.height / 2,
		}
		alignedFaces.push(newFace)
	}
	return alignedFaces
}
